import React, { useState, useEffect } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    Typography,
    Divider,
    message,
    Alert,
    Tag,
    Descriptions,
    Row,
    Col,
    Statistic,
} from "antd";
import {
    UserOutlined,
    MailOutlined,
    LockOutlined,
    SaveOutlined,
    CrownOutlined,
    TeamOutlined,
    CalendarOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { adminAPI } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const Profile = () => {
    const { user, updateUser } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [loadingSubscription, setLoadingSubscription] = useState(true);

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                name: user.name,
                email: user.email,
            });
            fetchSubscriptionInfo();
        }
    }, [user]);

    const fetchSubscriptionInfo = async () => {
        try {
            setLoadingSubscription(true);
            const response = await adminAPI.getSubscriptionInfo();
            setSubscriptionInfo(response.data);
        } catch (error) {
            console.error('Failed to fetch subscription info:', error);
        } finally {
            setLoadingSubscription(false);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await adminAPI.updateProfile(values);
            message.success(response.data.message || "Profile updated successfully");

            // Update session if user details changed
            if (response.data.user) {
                updateUser(response.data.user);
            }

            form.resetFields(["currentPassword", "newPassword", "confirmPassword"]);
        } catch (error) {
            console.error("Update profile error:", error);
            const errorMsg = error.response?.data?.error || "Failed to update profile";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <Title level={2}>Admin Profile</Title>
            <Text type="secondary">
                Manage your account settings, subscription plan, and password.
            </Text>
            <Divider />

            <Row gutter={[24, 24]}>
                {/* Subscription Info Section */}
                {user?.role !== 'super_admin' && (
                    <Col xs={24} lg={24}>
                        <Card 
                            title={
                                <span style={{ 
                                    fontSize: 18, 
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #001529 0%, #1890ff 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    <CrownOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                    Subscription Plan
                                </span>
                            }
                            bordered={false} 
                            className="shadow-sm"
                            loading={loadingSubscription}
                            style={{
                                borderTop: '3px solid',
                                borderImage: 'linear-gradient(90deg, #001529, #1890ff, #096dd9) 1',
                            }}
                        >
                            {subscriptionInfo && !subscriptionInfo.isSuperAdmin && (
                                <>
                                    {/* Status Alert */}
                                    {subscriptionInfo.subscription.isActive ? (
                                        subscriptionInfo.subscription.gracePeriod ? (
                                            <Alert
                                                type="info"
                                                showIcon
                                                icon={<ClockCircleOutlined />}
                                                message="Grace Period Active"
                                                description={`Your payment is under review. You have ${subscriptionInfo.subscription.daysRemaining} day(s) remaining until grace period expires.`}
                                                style={{ marginBottom: 20 }}
                                            />
                                        ) : (
                                            <Alert
                                                type="success"
                                                showIcon
                                                icon={<CheckCircleOutlined />}
                                                message="Subscription Active"
                                                description="Your subscription is active and you have full access to all features."
                                                style={{ marginBottom: 20 }}
                                            />
                                        )
                                    ) : (
                                        <Alert
                                            type="error"
                                            showIcon
                                            icon={<WarningOutlined />}
                                            message="Subscription Expired"
                                            description="Please submit payment to continue using the system."
                                            style={{ marginBottom: 20 }}
                                        />
                                    )}

                                    {/* Pricing Info */}
                                    <Row gutter={16} style={{ marginBottom: 24 }}>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" style={{ 
                                                background: 'linear-gradient(135deg, #001529 0%, #003a70 100%)', 
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(0,21,41,0.15)'
                                            }}>
                                                <Statistic
                                                    title={<span style={{ color: '#91caff' }}>Active Members</span>}
                                                    value={subscriptionInfo.pricing.memberCount}
                                                    prefix={<TeamOutlined />}
                                                    valueStyle={{ color: '#fff', fontWeight: 700 }}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" style={{ 
                                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', 
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(24,144,255,0.25)'
                                            }}>
                                                <Statistic
                                                    title={<span style={{ color: '#e6f7ff' }}>Monthly Amount</span>}
                                                    value={subscriptionInfo.pricing.currentAmount}
                                                    prefix="₹"
                                                    valueStyle={{ color: '#fff', fontWeight: 700 }}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Card size="small" style={{ 
                                                background: 'linear-gradient(135deg, #0050b3 0%, #1890ff 100%)', 
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(0,80,179,0.25)'
                                            }}>
                                                <Statistic
                                                    title={<span style={{ color: '#bae7ff' }}>{subscriptionInfo.subscription.gracePeriod ? "Grace Period Ends" : "Expires On"}</span>}
                                                    value={
                                                        subscriptionInfo.subscription.gracePeriod 
                                                            ? new Date(subscriptionInfo.subscription.gracePeriodExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                                                            : new Date(subscriptionInfo.subscription.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                                                    }
                                                    prefix={<CalendarOutlined />}
                                                    valueStyle={{ color: '#fff', fontSize: 20, fontWeight: 700 }}
                                                />
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Subscription Details */}
                                    

                                    {/* Pending Payment Info */}
                                    {subscriptionInfo.pendingPayment && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            style={{ marginTop: 16 }}
                                            message="Pending Payment"
                                            description={
                                                <div>
                                                    <div><strong>UTR:</strong> {subscriptionInfo.pendingPayment.utrId}</div>
                                                    <div><strong>Amount:</strong> ₹{subscriptionInfo.pendingPayment.amount}</div>
                                                    <div><strong>Submitted:</strong> {new Date(subscriptionInfo.pendingPayment.submittedAt).toLocaleString('en-IN')}</div>
                                                    {subscriptionInfo.pendingPayment.inGracePeriod ? (
                                                        <div style={{ color: '#52c41a', marginTop: 8 }}>
                                                            ✓ Grace period active until {new Date(subscriptionInfo.pendingPayment.gracePeriodExpiresAt).toLocaleDateString('en-IN')}
                                                        </div>
                                                    ) : (
                                                        <div style={{ color: '#ff4d4f', marginTop: 8 }}>
                                                            ⚠ Grace period expired - Awaiting admin approval
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        />
                                    )}
                                </>
                            )}

                            {subscriptionInfo?.isSuperAdmin && (
                                <Alert
                                    type="info"
                                    showIcon
                                    icon={<CrownOutlined />}
                                    message="Super Admin Account"
                                    description="You have unlimited access to all features. No subscription required."
                                />
                            )}
                        </Card>
                    </Col>
                )}

                {/* Profile Form Section */}
                <Col xs={24} lg={24}>
                    <Card bordered={false} className="shadow-sm">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{
                                name: user?.name,
                                email: user?.email,
                            }}
                        >
                            <Title level={4} style={{ marginBottom: 24 }}>
                                Personal Information
                            </Title>

                            <Form.Item
                                name="name"
                                label="Full Name"
                                rules={[{ required: true, message: "Please enter your name" }]}
                            >
                                <Input prefix={<UserOutlined />} placeholder="Full Name" size="large" />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label="Email Address"
                                rules={[
                                    { required: true, message: "Please enter your email" },
                                    { type: "email", message: "Please enter a valid email" },
                                ]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="Email Address" size="large" />
                            </Form.Item>

                            <Divider />

                            <Title level={4} style={{ marginBottom: 8 }}>
                                Change Password
                            </Title>
                            <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                                Leave new password fields empty if you don't want to change it.
                            </Text>

                            <Form.Item
                                name="newPassword"
                                label="New Password"
                                rules={[
                                    { min: 6, message: "Password must be at least 6 characters" },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="New Password"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label="Confirm New Password"
                                dependencies={["newPassword"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue("newPassword") === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                new Error("The two passwords do not match!")
                                            );
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirm New Password"
                                    size="large"
                                />
                            </Form.Item>

                            <Divider />

                            <Alert
                                message="Required for any changes"
                                description="Please enter your current password to authorize these changes."
                                type="warning"
                                showIcon
                                style={{ marginBottom: 24 }}
                            />

                            <Form.Item
                                name="currentPassword"
                                label="Current Password"
                                rules={[
                                    { required: true, message: "Current password is required to save changes" },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirm Current Password"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                    size="large"
                                    block
                                >
                                    Update Profile
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Profile;
